import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ExternalLink, Plus, Trash2, Upload, X } from 'lucide-react';
import { createGalleryImageVariants } from '../utils/gallery-image.js';
import '../styles/components/modal.css';

const PROJECT_TYPES = [
  { value: 'residential', label: 'Residencial' },
  { value: 'commercial', label: 'Comercial' },
  { value: 'expography', label: 'Expografia' },
];

function parseJsonArray(value) {
  try {
    const parsed = JSON.parse(value || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function normalizeGallery(project) {
  return parseJsonArray(project?.gallery_json).map((item) => (
    typeof item === 'string' ? { url: item, caption: '' } : {
      url: item.url || item.src || '',
      mobileUrl: item.mobileUrl || item.mobile_url || '',
      originalUrl: item.originalUrl || item.original_url || item.url || item.src || '',
      caption: item.caption || item.legenda || '',
    }
  )).filter((item) => item.url);
}

async function uploadImage(file, { responsiveGallery = false } = {}) {
  const formData = new FormData();
  formData.append('file', file);

  if (responsiveGallery) {
    try {
      const variants = await createGalleryImageVariants(file);
      if (variants.desktop.size > 5 * 1024 * 1024 || variants.mobile.size > 5 * 1024 * 1024) {
        throw new Error('Generated image variant exceeds the upload limit.');
      }
      formData.append('desktop', variants.desktop, 'desktop.webp');
      formData.append('mobile', variants.mobile, 'mobile.webp');
    } catch {
      // Keep the upload usable if client-side processing is unavailable.
    }
  }

  const response = await fetch('/api/uploads', {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Falha no envio da imagem.');
  if (!responsiveGallery) return data.url;
  return {
    url: data.url,
    mobileUrl: data.mobileUrl || '',
    originalUrl: data.originalUrl || data.url,
    caption: '',
  };
}

export default function ProjectModal({ project, onClose, onSave, onDelete }) {
  const [title, setTitle] = useState(project?.title || '');
  const [sector, setSector] = useState(project?.sector === 'museum' ? 'expography' : project?.sector || 'residential');
  const [city, setCity] = useState(project?.city || '');
  const [year, setYear] = useState(project?.year || '');
  const [featured, setFeatured] = useState(Boolean(project?.featured));
  const [summary, setSummary] = useState(project?.summary || '');
  const [body, setBody] = useState(project?.body || '');
  const [coverImage, setCoverImage] = useState(project?.cover_image || '');
  const [coverCaption, setCoverCaption] = useState(project?.cover_caption || '');
  const [useExternalCover, setUseExternalCover] = useState(false);
  const [externalCoverUrl, setExternalCoverUrl] = useState(/^https?:\/\//i.test(project?.cover_image || '') ? project.cover_image : '');
  const [gallery, setGallery] = useState(() => normalizeGallery(project));
  const [technicalSheet, setTechnicalSheet] = useState(() => parseJsonArray(project?.technical_sheet_json));
  const [videoUrl, setVideoUrl] = useState(project?.video_url || '');
  const [status, setStatus] = useState(project?.status || 'draft');
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const viewUrl = useMemo(() => project?.slug
    ? `/projects/${project.slug}${status === 'published' ? '' : '?preview=1'}`
    : '', [project?.slug, status]);

  const handleCoverUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploadingCover(true);
    setError('');
    try {
      setCoverImage(await uploadImage(file));
      setUseExternalCover(false);
      setExternalCoverUrl('');
    } catch (uploadError) {
      setError(uploadError.message);
    } finally {
      setUploadingCover(false);
    }
  };

  const handleGalleryUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    setUploadingGallery(true);
    setError('');
    try {
      for (const file of files) {
        const uploadedImage = await uploadImage(file, { responsiveGallery: true });
        setGallery((current) => [...current, uploadedImage]);
      }
    } catch (uploadError) {
      setError(uploadError.message);
    } finally {
      setUploadingGallery(false);
    }
  };

  const moveGalleryItem = (index, direction) => {
    setGallery((current) => {
      const next = [...current];
      const target = index + direction;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const updateTechnicalSheet = (index, key, value) => {
    setTechnicalSheet((current) => current.map((row, rowIndex) => (
      rowIndex === index ? { ...row, [key]: value } : row
    )));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    const coverImageToSave = useExternalCover ? externalCoverUrl.trim() : coverImage.trim();
    if (status === 'published' && !coverImageToSave) {
      setError('Adicione uma imagem de capa antes de publicar o projeto.');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        id: project?.id,
        title: title.trim(),
        sector,
        city: city.trim(),
        year: year ? Number(year) : null,
        featured,
        summary: summary.trim(),
        body: body.trim(),
        cover_image: coverImageToSave,
        cover_caption: coverCaption.trim(),
        gallery,
        technical_sheet: technicalSheet.filter((row) => row.label?.trim() || row.value?.trim()),
        video_url: videoUrl.trim(),
        status,
      });
    } catch (saveError) {
      setError(saveError.message || 'Não foi possível salvar o projeto.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!project || !onDelete) return;
    setSaving(true);
    setError('');
    try {
      await onDelete(project.id);
    } catch (deleteError) {
      setError(deleteError.message || 'Não foi possível excluir o projeto.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="modal__dialog modal__dialog--editor" role="dialog" aria-modal="true" aria-labelledby="project-form-title">
        <header className="modal__header">
          <div>
            <p className="modal__eyebrow">Projetos / {project ? 'Editar projeto' : 'Novo projeto'}</p>
            <h2 className="font-display modal__title" id="project-form-title">
              {project ? (title || 'Editar projeto') : 'Novo projeto'}
            </h2>
          </div>
          <div className="modal__header-actions">
            {viewUrl && <a className="btn btn--outline modal__view-link" href={viewUrl} target="_blank" rel="noreferrer"><ExternalLink size={15} /> Ver no site</a>}
            <button type="button" onClick={onClose} className="modal__close" aria-label="Fechar editor"><X size={22} /></button>
          </div>
        </header>

        <form onSubmit={handleSubmit} className="modal__editor-form">
          <div className="modal__editor-main">
            <section className="editor-panel">
              <h3>Informações principais</h3>
              <div className="form-field">
                <label htmlFor="project-title" className="font-label form-label">Nome do projeto</label>
                <input id="project-title" type="text" value={title} onChange={(event) => setTitle(event.target.value)} required maxLength={160} className="form-control" />
                <p className="form-hint">Título da página e nome no card da listagem.</p>
              </div>

              <fieldset className="form-field editor-type-field">
                <legend className="font-label form-label">Tipo</legend>
                <div className="editor-choices">
                  {PROJECT_TYPES.map((type) => (
                    <label key={type.value} className={`editor-choice ${sector === type.value ? 'is-selected' : ''}`}>
                      <input type="radio" name="project-sector" value={type.value} checked={sector === type.value} onChange={() => setSector(type.value)} />
                      {type.label}
                    </label>
                  ))}
                </div>
                <p className="form-hint">Define o filtro onde o projeto aparece e o rótulo acima do título.</p>
              </fieldset>

              <div className="modal__row modal__row--location">
                <div className="form-field">
                  <label htmlFor="project-city" className="font-label form-label">Cidade e estado</label>
                  <input id="project-city" type="text" value={city} onChange={(event) => setCity(event.target.value)} placeholder="Recife, PE" className="form-control" />
                </div>
                <div className="form-field">
                  <label htmlFor="project-year" className="font-label form-label">Ano</label>
                  <input id="project-year" type="number" min="1900" max="2100" value={year} onChange={(event) => setYear(event.target.value)} placeholder={String(new Date().getFullYear())} className="form-control" />
                </div>
              </div>
              <p className="form-hint">Cidade e ano aparecem no card e na ficha técnica.</p>

              <div className="form-field">
                <label htmlFor="project-summary" className="font-label form-label">Descrição curta</label>
                <textarea id="project-summary" value={summary} onChange={(event) => setSummary(event.target.value)} rows={3} maxLength={220} className="form-control" />
                <p className="form-hint">Uma ou duas frases. Aparece abaixo do título e nos cards. <span>{summary.length}</span> de 220 caracteres.</p>
              </div>

              <div className="form-field">
                <label htmlFor="project-body" className="font-label form-label">Texto do projeto</label>
                <textarea id="project-body" value={body} onChange={(event) => setBody(event.target.value)} rows={8} className="form-control" />
                <p className="form-hint">Conte a história do projeto. Use uma linha em branco para separar parágrafos.</p>
              </div>
            </section>

            <section className="editor-panel">
              <h3>Imagens</h3>
              <div className="editor-cover">
                <div className="editor-cover__preview">
                  {(useExternalCover ? externalCoverUrl : coverImage) ? <img src={useExternalCover ? externalCoverUrl : coverImage} alt="Prévia da capa do projeto" /> : <span>Sem capa</span>}
                </div>
                <div className="form-field editor-cover__content">
                  <span className="font-label form-label">Capa</span>
                  <p className="form-hint">Imagem grande no topo da página e foto do card. Prefira uma imagem horizontal.</p>
                  <label className="btn btn--outline editor-upload-button">
                    <Upload size={15} /> {uploadingCover ? 'Enviando...' : coverImage ? 'Trocar capa' : 'Enviar capa'}
                    <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleCoverUpload} disabled={uploadingCover} />
                  </label>
                  <label className="editor-external-cover-toggle">
                    <input type="checkbox" checked={useExternalCover} onChange={(event) => setUseExternalCover(event.target.checked)} />
                    Usar uma URL externa
                  </label>
                  {useExternalCover && (
                    <div className="form-field">
                      <label htmlFor="project-cover-url" className="font-label form-label">Endereço da imagem</label>
                      <input id="project-cover-url" aria-label="URL externa da imagem de capa" type="url" required value={externalCoverUrl} onChange={(event) => setExternalCoverUrl(event.target.value)} placeholder="https://..." className="form-control" />
                      <p className="form-hint">Use o endereço completo de uma imagem hospedada em outro site.</p>
                    </div>
                  )}
                  <label className="form-field">
                    <span className="font-label form-label">Legenda da capa (opcional)</span>
                    <input className="form-control" type="text" value={coverCaption} onChange={(event) => setCoverCaption(event.target.value)} maxLength={200} placeholder="Legenda exibida abaixo da imagem" />
                  </label>
                </div>
              </div>

              <div className="form-field editor-gallery-heading">
                <span className="font-label form-label">Galeria</span>
                <p className="form-hint">As legendas opcionais aparecem abaixo de cada imagem na página do projeto. Use as setas para definir a ordem.</p>
              </div>
              {gallery.length > 0 && (
                <div className="editor-gallery">
                  {gallery.map((image, index) => (
                    <article className="editor-gallery__item" key={`${image.url}-${index}`}>
                      <div className="editor-gallery__image"><img src={image.url} alt={image.caption || `Imagem ${index + 1}`} /><span>{index + 1}</span></div>
                      <input className="form-control" type="text" value={image.caption} placeholder="Legenda (opcional)" aria-label={`Legenda opcional da imagem ${index + 1}`} onChange={(event) => setGallery((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, caption: event.target.value } : item))} />
                      <div className="editor-gallery__tools">
                        <button type="button" className="btn btn--icon" aria-label={`Mover imagem ${index + 1} para cima`} disabled={index === 0} onClick={() => moveGalleryItem(index, -1)}><ArrowUp size={15} /></button>
                        <button type="button" className="btn btn--icon" aria-label={`Mover imagem ${index + 1} para baixo`} disabled={index === gallery.length - 1} onClick={() => moveGalleryItem(index, 1)}><ArrowDown size={15} /></button>
                        <button type="button" className="btn btn--icon btn--icon-danger" aria-label={`Remover imagem ${index + 1}`} onClick={() => setGallery((current) => current.filter((_, itemIndex) => itemIndex !== index))}><Trash2 size={14} /></button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
              <label className="editor-dropzone">
                <Plus size={20} />
                <span>{uploadingGallery ? 'Enviando imagens...' : 'Adicionar imagens'}</span>
                <small>JPG, PNG ou WebP. Cópias WebP ajustadas para desktop e celular serão geradas; o original será mantido.</small>
                <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleGalleryUpload} disabled={uploadingGallery} />
              </label>
            </section>

            <section className="editor-panel">
              <div className="form-field">
                <h3>Ficha técnica</h3>
                <p className="form-hint">Adicione os créditos que este projeto precisar. Linhas vazias não aparecem; cidade e ano são incluídos automaticamente.</p>
              </div>
              <div className="editor-sheet-head" aria-hidden="true"><span>Rótulo</span><span>Valor</span><span /></div>
              <div className="editor-sheet-rows">
                {technicalSheet.map((row, index) => (
                  <div className="editor-sheet-row" key={index}>
                    <input className="form-control" type="text" value={row.label || ''} placeholder="Ex.: Cliente" aria-label={`Rótulo da linha ${index + 1}`} onChange={(event) => updateTechnicalSheet(index, 'label', event.target.value)} />
                    <input className="form-control" type="text" value={row.value || ''} placeholder="Ex.: Prefeitura de Caruaru" aria-label={`Valor da linha ${index + 1}`} onChange={(event) => updateTechnicalSheet(index, 'value', event.target.value)} />
                    <button className="btn btn--icon btn--icon-danger" type="button" aria-label={`Remover linha ${index + 1}`} onClick={() => setTechnicalSheet((current) => current.filter((_, rowIndex) => rowIndex !== index))}><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
              <button className="btn btn--outline editor-add-row" type="button" onClick={() => setTechnicalSheet((current) => [...current, { label: '', value: '' }])}><Plus size={15} /> Adicionar linha</button>
            </section>

            <section className="editor-panel">
              <div className="form-field">
                <h3>Vídeo</h3>
                <label htmlFor="project-video" className="font-label form-label">Link do vídeo</label>
                <input id="project-video" type="url" value={videoUrl} onChange={(event) => setVideoUrl(event.target.value)} placeholder="https://www.youtube.com/..." className="form-control" />
                <p className="form-hint">Link do Instagram, YouTube ou Vimeo. Deixe vazio se não houver vídeo.</p>
              </div>
            </section>
          </div>

          <aside className="modal__editor-side">
            <section className="editor-panel editor-publication">
              <h3>Publicação</h3>
              <fieldset className="form-field editor-status-field">
                <legend className="font-label form-label">Status</legend>
                <label className="editor-status"><input type="radio" name="project-status" value="published" checked={status === 'published'} onChange={() => setStatus('published')} /> Publicado</label>
                <label className="editor-status"><input type="radio" name="project-status" value="draft" checked={status === 'draft'} onChange={() => setStatus('draft')} /> Rascunho</label>
                <p className="form-hint">Rascunho fica salvo no painel, mas não aparece no site.</p>
              </fieldset>
              <label className="editor-featured">
                <input type="checkbox" checked={featured} onChange={(event) => setFeatured(event.target.checked)} />
                <span><strong>Destaque na página inicial</strong><small>Exibe este projeto na faixa de destaque da home.</small></span>
              </label>
              {error && <p className="editor-error" role="alert">{error}</p>}
              <button type="submit" className="btn btn--solid editor-save" disabled={saving || uploadingCover || uploadingGallery}>{saving ? 'Salvando...' : 'Salvar projeto'}</button>
              <button type="button" className="btn btn--outline editor-cancel" onClick={onClose} disabled={saving}>Cancelar</button>
            </section>

            {project && (
              <section className="editor-panel editor-danger-zone">
                <h3>Excluir projeto</h3>
                <p className="form-hint">Remove o projeto do site. Esta ação não pode ser desfeita.</p>
                <button type="button" className="btn btn--outline btn--icon-danger editor-delete" onClick={handleDelete} disabled={saving}><Trash2 size={14} /> Excluir projeto</button>
              </section>
            )}
          </aside>
        </form>
      </div>
    </div>
  );
}
